import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from '@angular/core';
import { environment } from 'environments/environment';
import { ReportsService } from '~/services/modules/reports/reports';
import { ThemeManagerService } from '~/services/theme-manager';
import { LazyLoadingLibraryService } from '~/modules/stocks/reports/report/LazyLoadingLibraryService';
import { LabelService } from '~/services/modules/stocks/labels';
import { LabelPreset } from '~/models/modules/labelPreset';

/**
 * BarcodeReportViewerComponent
 *
 * Handles all barcode/label report generation previously embedded inside
 * ReportViewerComponent. Extract the barcode branch of initPage() here so
 * the parent component stays focused on standard (non-barcode) reports.
 *
 * Usage:
 *   <barcode-report-viewer
 *     [reportObject]="reportObject"
 *     [barcodeModel]="barcodeModel"
 *     [isCatalog]="isCatalog"
 *     [previewMode]="previewMode"
 *     [windowMode]="windowMode"
 *     (returnDefinition)="onReturnDefinition($event)"
 *     (maximizeWindow)="onMaximizeWindow()">
 *   </barcode-report-viewer>
 */
@Component({
    selector: 'barcode-report-viewer',
    templateUrl: './barcode-report-viewer.componant.html',
    styleUrls: ['./barcode-report-viewer.componant.css'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BarcodeReportViewerComponent implements OnInit, OnChanges {

  // ─── Inputs ────────────────────────────────────────────────────────────────

  @Input() reportObject: any;
  @Input() barcodeModel: any;
  @Input() isCatalog: boolean = false;
  @Input() previewMode: boolean = false;
  @Input() windowMode: boolean = true;
  @Input() showSelectRecords: boolean = false;

  // ─── Outputs ───────────────────────────────────────────────────────────────

  @Output() returnDefinition = new EventEmitter<any>();
  @Output() maximizeWindow = new EventEmitter<void>();

  // ─── Internal state ────────────────────────────────────────────────────────

  _windowMode: boolean = true;
  _reportObject: any;
  _showSelectRecords: boolean = false;

  public dataSet: any;
  public dataSetFinal: any;

  stimulsoftReport: any;
  viewer: any;           // Stimulsoft.Viewer.StiViewer
  options: any;          // Stimulsoft.Viewer.StiViewerOptions

  reportFileLoaded = false;
  viewerFileLoaded = false;
  designerFileLoaded = false;

  // ─── NEW state: Record Selection ────────────────────────────────────────────

  /** Whether the selection panel is open. */
  isSelectRecordsVisible: boolean = false;

  /** Live search string typed in the panel's search box. */
  selectSearchTerm: string = '';

  /**
   * Subset of allReportData that matches the current search.
   * Displayed in the grid so the user can search before selecting.
   */
  filteredSelectData: any[] = [];

  /**
   * The records the user has checked.
   * Set by Kendo Grid's (selectedKeysChange) or manual checkbox tracking.
   */
  selectedRecords: any[] = [];

  /**
   * Column definitions derived at runtime from allReportData.
   * Used to build the selection grid dynamically.
   */
  selectGridColumns: { field: string; title: string }[] = [];

  /** Original full dataset captured. */
  allReportData: any[] = [];

  /** Keeps track of the last rendering call so we can re-execute it. */
  private _lastRenderCall: { name: string; args: any[] } | null = null;

  // ─── Constructor ───────────────────────────────────────────────────────────

  constructor(
    private _themeManager: ThemeManagerService,
    private _lazyLoadingLibraryService: LazyLoadingLibraryService,
    private _reportService: ReportsService,
    private _labelsService: LabelService,
  ) { }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this._windowMode = this.windowMode;
    this._reportObject = this.reportObject;
    this._showSelectRecords = this.showSelectRecords;

    if (this.barcodeModel && !this.barcodeModel.numberOfCopy) {
      this.barcodeModel.numberOfCopy = 1;
    }
  }

  ngAfterViewInit(): void {
    this._lazyLoadingLibraryService
      .loadJs('assets/scripts/stimulsoft.reports.pack.js')
      .subscribe(() => {
        this.reportFileLoaded = true;
        if (this.viewerFileLoaded && this.designerFileLoaded) this._initPage();
      });

    this._lazyLoadingLibraryService
      .loadJs('assets/scripts/stimulsoft.viewer.pack.js')
      .subscribe(() => {
        this.viewerFileLoaded = true;
        if (this.reportFileLoaded && this.designerFileLoaded) this._initPage();
      });

    this._lazyLoadingLibraryService
      .loadJs('assets/scripts/stimulsoft.designer.pack.js')
      .subscribe(() => {
        this.designerFileLoaded = true;
        if (this.reportFileLoaded && this.viewerFileLoaded) this._initPage();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showSelectRecords) {
      this._showSelectRecords = this.showSelectRecords;
    }
    if (
      (changes.reportObject && !changes.reportObject.firstChange) ||
      (changes.barcodeModel && !changes.barcodeModel.firstChange)
    ) {
      this._reportObject = this.reportObject;
      this._windowMode = this.windowMode;
      this._initPage();
    }
  }

  // ─── Initialisation ────────────────────────────────────────────────────────

  private _initPage(): void {
    this._initStimulsoft();

    this.dataSet = new Stimulsoft.System.Data.DataSet('DataSet');

    if (this.isCatalog) {
      this._handleCatalogBarcode();
    } else if (!this.previewMode) {
      this._handleEditBarcode();
    } else {
      this._handlePrePrintBarcode();
    }
  }

  private _initStimulsoft(): void {
    Stimulsoft.Base.StiLicense.key =
      '6vJhGtLLLz2GNviWmUTrhSqnOItdDwjBylQzQcAOiHkUqG1JqVi9xF1CsQtSylmNCw08f5US/nThDcH2mIyIhcb9q2' +
      'eL238CYd2WruGTJRQWTjv4Ojri9sZfpmunjsBAwTcJRB+7bLz0yAVCU6zESyCSrqmTsZGqSSjXhq82ybmFzAFUskAo' +
      '4HStx+kugphgl4gcDP0VI2kgHB6uQSOD2Xh8X+fmtkH1Q6RMplr+plgH0lQ35cSNk7kDJvc07nTPGVv+ixfOJYHyWO' +
      'o+A6M93S04A7HjpQFnJHCQqzJ7lJ3ZO9heRh2Vt5VBfUJoQZIFTpTNCAvXKZ6FmbFtIa8lKG0CGWFBJjDFkh6e6yVf' +
      'IRPFT48DDBGyC1ZZXHFTiCSxTxx9Rn+ZQrLDgvhZa4YFVIBm0Rrekr67eVHUqXpL7IiQA6kbCkJ/LZ6LCJwEL0TbBn' +
      'RWDfcx/r65mXVC/HoS+KZ3MWfB1fqZmiLfeWjTXXvnE3LjxgJYGobOqvdZns8d9c/Xq+cZtW6/wjnZL6SrHSjaUJPc' +
      'rLgViTggF/EzQGigT6qbzY1Cf5YNhWCuYaN+ou0vG9LVmMSR6oPxc5eHphDFlzEVgUHZWeyGlX0AVp7HVFVWUiJevw' +
      '9NbBkdYnR+0Iqs9pwBbvH3fgbFM8C9eyWM41wmJl16/jqohlJZHuw=';

    this.stimulsoftReport = new Stimulsoft.Report.StiReport();
    this.options = new Stimulsoft.Viewer.StiViewerOptions();
    this.viewer = new Stimulsoft.Viewer.StiViewer(this.options, 'StiViewer', false);
  }

  // ─── Routing helpers ───────────────────────────────────────────────────────

  /** Catalog barcode preview (isCatalog === true) */
  private _handleCatalogBarcode(): void {
    if (!this._reportObject || this._reportObject.ReportObj.windowMode) return;

    this._reportObject.ReportObj.addLogo = this._reportObject.ReportObj.addLogo === 'Yes';
    this._reportObject.ReportObj.addAddress = this._reportObject.ReportObj.addAddress === 'Yes';

    if (
      this._reportObject.ReportObj.dataSource?.includes('GetReportbyDataSourceName') &&
      this._reportObject.ReportObj.selectedFields
    ) {
      this._reportObject.ReportObj.selectedFields = this.getDistinctSelectFieldsString(
        this._reportObject.ReportObj.selectedFields
      );
      this._reportService
        .PreviewReportData(this._reportObject.ReportObj)
        
        .subscribe((response: any) => {
          setTimeout(() => this.createBarcodeCatalogPreview(response), 200);
        });
    }
  }

  /** Edit / upsert barcode (previewMode === false) */
  private _handleEditBarcode(): void {
    if (!this._reportObject?.ReportObj) return;

    this._reportObject.ReportObj.addLogo = this._reportObject.ReportObj.addLogo === 'Yes';
    this._reportObject.ReportObj.addAddress = this._reportObject.ReportObj.addAddress === 'Yes';

    const hasDefinition = !!this._reportObject.ReportObj.definition;
    const dataSource = this._reportObject.ReportObj.dataSource ?? '';

    if (!dataSource.includes('GetReportbyDataSourceName')) {
      return;
    }

    if (!this._reportObject.ReportObj.selectedFields) {
      return;
    }

    this._reportObject.ReportObj.selectedFields = this.getDistinctSelectFieldsString(
      this._reportObject.ReportObj.selectedFields
    );

    this._reportService
      .PreviewReportData(this._reportObject.ReportObj)
      
      .subscribe((response: any) => {
        setTimeout(() => {
          this.barcodeModel.Row = 1;
          this.barcodeModel.Column = 1;

          if (hasDefinition) {
            this.openReportBarcodeLabelWithReportDefinitionForEditBarcode(this._reportObject, response);
          } else {
            this.createReportbarcodePreview_3(
              response,
              this.barcodeModel.LabelSizeID,
              this.barcodeModel.Row,
              this.barcodeModel.Column,
              null,
              this.barcodeModel.LabelSize,
              this.barcodeModel.isQr ?? false,
            );
          }
        }, 200);
      });
  }

  /** Pre-print barcodes (previewMode === true) */
  private _handlePrePrintBarcode(): void {
    if (this.barcodeModel.customLabelPreset && this.barcodeModel.transactionId) {
      this._handleCustomLabelPreset();
    } else {
      this.barcodePrintBarcodes(
        this.barcodeModel.BarcodeValues,
        this.barcodeModel.LabelSizeID,
        this.barcodeModel.Row,
        this.barcodeModel.Column,
        null,
        this.barcodeModel.LabelSize,
        this.barcodeModel.numberOfCopy,
      );
    }
  }

  private _handleCustomLabelPreset(): void {
    const presetId =
      this.barcodeModel.LabelPresetId && typeof this.barcodeModel.LabelPresetId === 'object'
        ? (this.barcodeModel.LabelPresetId.id ?? this.barcodeModel.LabelPresetId.Id)
        : this.barcodeModel.LabelPresetId;

    this._labelsService.getLabelPresetById(presetId).subscribe((labelPreset: LabelPreset) => {
      {
      this._reportService
        .GetReportDataForBarcodeLabelDesigner(
            presetId,
          this.barcodeModel.transactionId,
          this.barcodeModel?.tableName,
          this.barcodeModel.isOneLineItem === false,
          labelPreset.selectedFields
        )
        
        .subscribe((response: any) => {
          let finalResponse = response as Array<any>;
          if (this.barcodeModel.isOneLineItem === false && Array.isArray(finalResponse)) {
            const expanded: any[] = [];
            finalResponse.forEach((item: any) => {
              const qtyKey = Object.keys(item).find(
                key => key.toLowerCase() === 'quantityreceived' || key.toLowerCase() === 'quantity'
              );
              const qty = qtyKey ? Number(item[qtyKey]) : 1;
              const times = isNaN(qty) || qty <= 0 ? 1 : Math.floor(qty);
              for (let i = 0; i < times; i++) {
                expanded.push({ ...item });
              }
            });
            finalResponse = expanded;
          }
          this.openReportBarcodeLabelWithReportDefinitionWithoutReportObject(
            finalResponse,
            labelPreset.reportDefinition,
            false,
            labelPreset
          );
        });
      }
    });
  }

  // ─── Catalog barcode preview ───────────────────────────────────────────────

  createBarcodeCatalogPreview(response: any, isReRendering = false): void {
    debugger
    if (!isReRendering) {
      this.allReportData = response || [];
      this._lastRenderCall = { name: 'createBarcodeCatalogPreview', args: [response] };
      this._resetSelectionPanel();
    }
    this.stimulsoftReport.reportUnit = Stimulsoft.Report.StiReportUnitType.Inches;
    const that = this;
    const allFields: string[] = [];
    const allbarcodeFields: string[] = [];

    that._reportObject.selectedFields.forEach((el: any) => {
      const isBarcode = el.title && (el.title.includes('(barcode image)') || el.title.includes('(QRCode image)'));
      const suffix = el.title && el.title.includes('(QRCode image)') ? '(QRCode image)' : '(barcode image)';
      if (!isBarcode) allFields.push(el.field);
      if (isBarcode) allbarcodeFields.push(el.field + suffix);
    });

    if (response?.length > 0) {
      response = response.slice(0, 20);
      const isUrl = this._isUrlEnabled();
      const isQR = this._isQR();
      const mapped = response.map((item: any) => {
        const obj: any = {};
        allFields.forEach(f => (obj[f] = this._resolveItemValue(item, f)));
        allbarcodeFields.forEach(f => {
          if (isQR && isUrl) {
            obj[f] = this._buildQrUrl(item, f);
          } else {
            obj[f] = this._resolveItemValue(item, f);
          }
        });
        return obj;
      });
      this.dataSet.readJson(mapped);
    } else if (response != null) {
      this.dataSet.readJson(this._buildEmptyModel(allFields, allbarcodeFields));
    }

    this._applyAddressAndLogo(this._reportObject.ReportObj);

    this.stimulsoftReport.regData(this.dataSet.dataSetName, '', this.dataSet);

    const page = this._setupPage(this._reportObject.ReportObj);

    this._buildBandStructure(page, allFields, allbarcodeFields, this._reportObject, true);

    this.stimulsoftReport.dictionary.synchronize();
    this.viewer.report = this.stimulsoftReport;

    if (this._isDefinitionOnly()) {
      this.returnDefinition.emit(
        this.stimulsoftReport.saveEncryptedReportToString(environment.reportsKey)
      );
    } else {
      this.viewer.renderHtml('viewer');
    }
  }

  // ─── Dynamic barcode label (no saved definition) ───────────────────────────

  private _normalizeLabelSize(LabelSize: any): any {
    if (!LabelSize) {
      LabelSize = this.barcodeModel?.LabelSize || {};
    }
    return {
      ...LabelSize,
      pageWidth: LabelSize?.pageWidth ?? LabelSize?.width ?? this.barcodeModel?.Width ?? 4,
      pageHeight: LabelSize?.pageHeight ?? LabelSize?.height ?? this.barcodeModel?.Height ?? 2,
      width: LabelSize?.width ?? this.barcodeModel?.Width ?? 4,
      height: LabelSize?.height ?? this.barcodeModel?.Height ?? 2,
      columns: LabelSize?.columns ?? 1,
      rows: LabelSize?.rows ?? 1,
      columnGaps: LabelSize?.columnGaps ?? 0,
      verticalPitch: LabelSize?.verticalPitch ?? 0,
      verticalMargin: LabelSize?.verticalMargin ?? 0,
      horizontalMargin: LabelSize?.horizontalMargin ?? 0,
      pageLeftMargin: LabelSize?.pageLeftMargin ?? 0,
      pageBottomMargin: LabelSize?.pageBottomMargin ?? 0,
      pageRightMargin: LabelSize?.pageRightMargin ?? 0,
      pageTopMargin: LabelSize?.pageTopMargin ?? 0,
    };
  }

  createReportbarcodePreview_3(
    response: any,
    LabelSizeID: number = null,
    Row: number = null,
    Column: number = null,
    LabelPresetId: number = null,
    LabelSize: any = null,
    isQR = false,
    isReRendering = false,
  ): void {
    LabelSize = this._normalizeLabelSize(LabelSize);
    if (!isReRendering) {
      this.allReportData = response || [];
      this._lastRenderCall = {
        name: 'createReportbarcodePreview_3',
        args: [response, LabelSizeID, Row, Column, LabelPresetId, LabelSize, isQR]
      };
      this._resetSelectionPanel();
    }
    Row = 1;
    Column = 1;

    this.stimulsoftReport.reportUnit = Stimulsoft.Report.StiReportUnitType.Inches;
    this.stimulsoftReport.width = LabelSize.pageWidth;
    this.stimulsoftReport.height = LabelSize.pageHeight;

    const { allFields, allbarcodeFields } = this._collectFields();
    this.dataSet.readJson(this._mapResponse(response, allFields, allbarcodeFields));

    this.stimulsoftReport.regData(this.dataSet.dataSetName, '', this.dataSet);

    const page = this.stimulsoftReport.pages.getByIndex(0);
    const Zebra = this._isZebra(LabelSizeID);
    const HeightCode = this._calcHeightCode(LabelSize.height, allFields, allbarcodeFields);

    this._applyLabelPageSettings(page, LabelSize);

    const dataBand = this._createLabelDataBand(page, LabelSize, LabelSizeID);

    let posY = 0;
    let nameIndex = 1;

    for (const index in this.dataSet.tables.list[0].columns.list) {
      const dataColumn = this.dataSet.tables.list[0].columns.list[index];
      const { name, isBc, rawName } = this._stripBarcodeFlag(dataColumn);

      if (!Zebra) {
        this._addLabelComponent(dataBand, name, isBc, posY, LabelSize.width, HeightCode, isQR, nameIndex, LabelSizeID, rawName);
      } else {
        const rectX = LabelSizeID === 12 ? -0.034 : 0;
        this._addZebraComponent(dataBand, name, isBc, posY, rectX, LabelSize.width, HeightCode, isQR, nameIndex, LabelSizeID, rawName);
      }

      posY += HeightCode;
      nameIndex++;
    }

    page.invokeRendering();
    this._windowMode = true;
    this.options.toolbar.visible = true;
    this.options.toolbar.zoom = 0;
    this.stimulsoftReport.dictionary.synchronize();
    this.viewer.report = this.stimulsoftReport;
    this.viewer.renderHtml('viewer');
    this.returnDefinition.emit(
      this.stimulsoftReport.saveEncryptedReportToString(environment.reportsKey)
    );
  }

  // ─── Barcode label with existing definition (update/patch) ─────────────────

  private createReportbarcodePreviewWithReportDefinition(
    response: any,
    LabelSizeID: number = null,
    Row: number = null,
    Column: number = null,
    LabelPresetId: number = null,
    LabelSize: any = null,
    isQR = false,
  ): void {
    LabelSize = this._normalizeLabelSize(LabelSize);
    this.stimulsoftReport.reportUnit = Stimulsoft.Report.StiReportUnitType.Inches;
    this.stimulsoftReport.width = LabelSize.pageWidth;
    this.stimulsoftReport.height = LabelSize.pageHeight;

    const { allFields, allbarcodeFields } = this._collectFields();
    this.dataSet.readJson(this._mapResponse(response, allFields, allbarcodeFields));
    this.stimulsoftReport.regData(this.dataSet.dataSetName, '', this.dataSet);

    const page = this.stimulsoftReport.pages.getByIndex(0);
    const dataBand = page.components.list.find((c: any) => c?.name === 'DataBand');
    const Zebra = this._isZebra(LabelSizeID);
    const HeightCode = this._calcHeightCode(LabelSize.height, allFields, allbarcodeFields);

    this._applyLabelPageSettings(page, LabelSize);

    let posY = 0;
    let nameIndex = 1;

    for (const index in this.dataSet.tables.list[0].columns.list) {
      const dataColumn = this.dataSet.tables.list[0].columns.list[index];
      const { name, isBc, rawName } = this._stripBarcodeFlag(dataColumn);

      if (isBc) {
        const barcode = this._makeBarcodeEl(rawName, 0, posY, LabelSize.width, HeightCode, isQR, nameIndex, LabelSizeID);
        const existBc = dataBand.components.list.find(
          (x: any) => x.name.includes('DataText') && x.code === barcode.code
        );
        if (!existBc) {
          dataBand.components.add(barcode);
        } else {
          existBc.height = HeightCode;
          existBc.top = posY;
          existBc.barCodeType = isQR ? new Stimulsoft.Report.BarCodes.StiQRCodeBarCodeType() : new Stimulsoft.Report.BarCodes.StiCode128AutoBarCodeType(10, HeightCode);
        }
      } else {
        const textEl = this._makeTextEl(name, 0, posY, LabelSize.width, HeightCode, nameIndex);
        const existTx = dataBand.components.list.find(
          (x: any) => x.name.includes('DT') && x.text === textEl.text
        );
        if (!existTx) {
          dataBand.components.add(textEl);
        } else {
          existTx.height = HeightCode;
          existTx.top = posY;
        }
      }

      posY += HeightCode;
      nameIndex++;
    }

    page.invokeRendering();
    this.returnDefinition.emit(
      this.stimulsoftReport.saveEncryptedReportToString(environment.reportsKey)
    );
  }

  // ─── Pre-print barcodes (Avery / Zebra system labels) ─────────────────────

  barcodePrintBarcodes(
    BarcodeValues: any[],
    LabelSizeID: number,
    Row: number,
    Column: number,
    LabelPresetId: number = null,
    LabelSize: any,
    NumberOfCopies: number,
    isReRendering = false,
  ): void {
    if (!isReRendering) {
      this.allReportData = BarcodeValues || [];
      this._lastRenderCall = {
        name: 'barcodePrintBarcodes',
        args: [BarcodeValues, LabelSizeID, Row, Column, LabelPresetId, LabelSize, NumberOfCopies]
      };
      this._resetSelectionPanel();
    }
    if (!NumberOfCopies) NumberOfCopies = 1;

    this.stimulsoftReport.reportUnit = Stimulsoft.Report.StiReportUnitType.Inches;
    const page = this.stimulsoftReport.pages.getByIndex(0);
    const Zebra = this._isZebra(LabelSizeID);

    const {
      width: _Width, height: _Height, pageWidth: _PageWidth, pageHeight: _PageHeight,
      verticalPitch: VerticalPitch, verticalMargin: VerticalMargin, horizontalMargin: HorizontalMargin,
      columns: Columns, rows: Rows,
      pageLeftMargin, pageBottomMargin, pageRightMargin,
    } = LabelSize;

    page.margins.left = pageLeftMargin;
    page.margins.bottom = pageBottomMargin;
    page.margins.right = pageRightMargin;
    page.margins.top = VerticalMargin;
    page.unlimitedHeight = false;

    page._pageWidth = _PageWidth;
    page._pageHeight = _PageHeight;
    page.pageWidth = _PageWidth;
    page.pageHeight = _PageHeight;

    if (!Zebra) {
      BarcodeValues = this.handleAveryMultipleCopies(BarcodeValues, NumberOfCopies);
    } else {
      page._numberOfCopies = NumberOfCopies;
    }

    this.dataSet.readJson(this._enrichResponse(BarcodeValues));
    this.stimulsoftReport.regData(this.dataSet.dataSetName, '', this.dataSet);
    this.stimulsoftReport.dictionary.synchronize();

    if (!Zebra) {
      this._printAvery(page, BarcodeValues, LabelSizeID, LabelSize, Row, Column, false);
    } else {
      this._printZebraPrePrint(page, BarcodeValues, LabelSizeID, LabelSize, false);
    }

    this.viewer.report = this.stimulsoftReport;
    this.maximizeWindow.emit();
    this.viewer.renderHtml('viewer');
  }

  // ─── Open with pre-saved definition (no reportObject) ─────────────────────

  openReportBarcodeLabelWithReportDefinitionWithoutReportObject(
    response: any,
    reportDefinition: any,
    isReRendering = false,
    labelPreset: any = null,
  ): void {
    if (!isReRendering) {
      this.allReportData = response || [];
      this._lastRenderCall = {
        name: 'openReportBarcodeLabelWithReportDefinitionWithoutReportObject',
        args: [response, reportDefinition, false, labelPreset]
      };
    }

    if (!this._reportObject && labelPreset) {
      let fieldsArr: any[] = [];
      if (labelPreset.selectedFields) {
        if (typeof labelPreset.selectedFields === 'string') {
          fieldsArr = labelPreset.selectedFields.split(',').filter(Boolean).map((f: string) => ({
            field: f.trim(),
            title: f.trim()
          }));
        } else if (Array.isArray(labelPreset.selectedFields)) {
          fieldsArr = labelPreset.selectedFields.map((f: any) => {
            if (typeof f === 'string') {
              return { field: f, title: f };
            }
            return { field: f.field || f.name, title: f.title || f.name || f.field };
          });
        }
      }

      this._reportObject = {
        ReportObj: {
          name: labelPreset.name || null,
          siteId: this.barcodeModel?.siteId || null,
          customLabel: labelPreset.customLabel || false,
          LabelPreset: labelPreset,
          dataSource: labelPreset.sourceView,
          addAddress: "No",
          addLogo: "No",
          groupBy: "",
          header: "",
          orientation: "Portrait",
          selectedFields: fieldsArr,
          definition: reportDefinition,
        },
        selectedFields: fieldsArr
      };
    }

    if (!isReRendering) {
      this._resetSelectionPanel();
    }

    this.dataSet = new Stimulsoft.System.Data.DataSet('DataSet');
    this.stimulsoftReport = new Stimulsoft.Report.StiReport();

    if (reportDefinition) {
      this._loadReportDefinition(reportDefinition);
    }

    response = this._skipRowsAndColumns(response);

    const page = this.stimulsoftReport.pages.getByIndex(0);
    if (page && labelPreset && labelPreset.columns > 1) {
      const dataBand = page.components.list.find(
        (c: any) => c.name === 'DataBand' || c.name?.includes('DataBand') || (c.dataSourceName === 'root' || c.dataSourceName?.includes('root'))
      );
      if (dataBand) {
        dataBand.columns = labelPreset.columns;
        dataBand.columnWidth = labelPreset.Width || 2.5;
        dataBand.columnGaps = 0.1;
      }
      if (page.pageWidth < 8) {
        page.pageWidth = 8.5;
        page.pageHeight = 11;
        page._pageWidth = 8.5;
        page._pageHeight = 11;
        page.margins.left = 0.25;
        page.margins.right = 0.25;
        page.margins.top = 0.25;
        page.margins.bottom = 0.25;
      }
    }

    this.dataSet.readJson(this._enrichResponse(response));
    this.options.toolbar.visible = true;
    if (page) {
      page.orientation = Stimulsoft.Report.Components.StiPageOrientation.Portrait;
    }
    this.stimulsoftReport.regData(this.dataSet.dataSetName, '', this.dataSet);
    this.stimulsoftReport.dictionary.synchronize();

    this.viewer = new Stimulsoft.Viewer.StiViewer(this.options, 'StiViewer', false);
    this.viewer.report = this.stimulsoftReport;
    this.maximizeWindow.emit();
    this.viewer.renderHtml('viewer');
  }

  // ─── Open with definition from reportObject ────────────────────────────────

  openReportBarcodeLabelWithReportDefinition(
    reportObject: any,
    response: any,
    isReRendering = false,
  ): void {
    if (!isReRendering) {
      this.allReportData = response || [];
      this._lastRenderCall = {
        name: 'openReportBarcodeLabelWithReportDefinition',
        args: [reportObject, response]
      };
      this._resetSelectionPanel();
    }
    if (reportObject.ReportObj.definition) {
      this._loadReportDefinition(reportObject.ReportObj.definition);
    }

    response = this._skipRowsAndColumns(response);

    const LabelSize = this.barcodeModel.LabelSize;
    const page = this.stimulsoftReport.pages.getByIndex(0);
    const Zebra = !LabelSize.id || (LabelSize.Id >= 11 && LabelSize.id < 22);

    const labelPreset = reportObject?.ReportObj?.LabelPreset;
    if (page && labelPreset && labelPreset.columns > 1) {
      const dataBand = page.components.list.find(
        (c: any) => c.name === 'DataBand' || c.name?.includes('DataBand') || (c.dataSourceName === 'root' || c.dataSourceName?.includes('root'))
      );
      if (dataBand) {
        dataBand.columns = labelPreset.columns;
        dataBand.columnWidth = labelPreset.Width || 2.5;
        dataBand.columnGaps = 0.1;
      }
      if (page.pageWidth < 8) {
        page.pageWidth = 8.5;
        page.pageHeight = 11;
        page._pageWidth = 8.5;
        page._pageHeight = 11;
        page.margins.left = 0.25;
        page.margins.right = 0.25;
        page.margins.top = 0.25;
        page.margins.bottom = 0.25;
      }
    }

    if (!Zebra && this.barcodeModel.numberOfCopy > 1) {
      response = this.handleAveryMultipleCopies(response, this.barcodeModel.numberOfCopy);
    } else {
      if (page) page._numberOfCopies = this.barcodeModel.numberOfCopy;
    }

    this.dataSet.readJson(this._enrichResponse(response));
    this.options.toolbar.visible = true;
    if (page) {
      page.orientation = Stimulsoft.Report.Components.StiPageOrientation.Portrait;
    }
    this.stimulsoftReport.regData(this.dataSet.dataSetName, '', this.dataSet);
    this.stimulsoftReport.dictionary.synchronize();
    this.viewer.report = this.stimulsoftReport;
    this.maximizeWindow.emit();
    this.viewer.renderHtml('viewer');
  }

  // ─── Open/edit barcode with existing definition (edit mode) ───────────────

  openReportBarcodeLabelWithReportDefinitionForEditBarcode(
    reportObject: any,
    response: any,
    isReRendering = false,
  ): void {
    if (!isReRendering) {
      this.allReportData = response || [];
      this._lastRenderCall = {
        name: 'openReportBarcodeLabelWithReportDefinitionForEditBarcode',
        args: [reportObject, response]
      };
      this._resetSelectionPanel();
    }
    this.dataSetFinal = new Stimulsoft.System.Data.DataSet('DataSet');

    if (reportObject.ReportObj.definition) {
      this._loadReportDefinition(reportObject.ReportObj.definition);
    }

    setTimeout(() => {
      response = this._skipRowsAndColumns(response);

      const LabelSize = this.barcodeModel.LabelSize;
      const page = this.stimulsoftReport.pages.getByIndex(0);
      page._numberOfCopies = this.barcodeModel.numberOfCopy;

      let dataBand = page.components.list.find((c: any) => c?.name === 'DataBand') ?? {};

      const { newFieldsDetected, listFieldNames, listBarcodeFieldNames } =
        this._detectFieldChanges(dataBand);

      // Remove stale fields from the databand
      if (dataBand.components) {
        dataBand.components.list = dataBand.components.list.filter(
          (x: any) =>
            (x.name.includes('DT') && listFieldNames.includes(x.text)) ||
            (x.name.includes('DataText') && listBarcodeFieldNames.includes(x.code))
        );
      }

      const LabelSizeChanged = this._detectLabelSizeChange();

      if (LabelSizeChanged) {
        page.components.remove(dataBand);
        dataBand = new Stimulsoft.Report.Components.StiDataBand();
        dataBand.dataSourceName = 'root';
        dataBand.height = page.height;
        dataBand.name = 'DataBand';

        if (this.barcodeModel.LabelSizeID) {
          dataBand.columns = LabelSize.columns;
          dataBand.columnWidth = LabelSize.width;
          dataBand.columnGaps = LabelSize.columnGaps;
        }
        page.components.add(dataBand);
      }

      if (newFieldsDetected || LabelSizeChanged) {
        this.stimulsoftReport.dictionary.databases.clear();
        this.stimulsoftReport.dictionary.dataSources.clear();
        this.createReportbarcodePreviewWithReportDefinition(
          response,
          this.barcodeModel.LabelSizeID,
          this.barcodeModel.Row,
          this.barcodeModel.Column,
          null,
          this.barcodeModel.LabelSize,
          this.barcodeModel.isQr ?? false,
        );
      } else {
        if (response?.length > 0) {
          response = response.slice(0, 20);
          this.dataSet.readJson(this._enrichResponse(response));
          this.stimulsoftReport.regData(this.dataSet.dataSetName, '', this.dataSet);
          this.returnDefinition.emit(reportObject.ReportObj.definition);
        }
      }

      this.options.toolbar.visible = true;
      page.invokeRendering();
      this._windowMode = true;
      this.options.toolbar.zoom = 0;
      this.stimulsoftReport.pages.getByIndex(0).orientation =
        Stimulsoft.Report.Components.StiPageOrientation.Portrait;
      this.stimulsoftReport.dictionary.synchronize();
      this.viewer.report = this.stimulsoftReport;

      const viewerEl = document.getElementById('viewer');
      if (!viewerEl) {
        setTimeout(() => { /* viewer not yet in DOM */ }, 1000);
      } else {
        this.viewer.renderHtml('viewer');
      }
    }, 300);
  }

  // ─── Avery pre-print ───────────────────────────────────────────────────────

  private _printAvery(
    page: any,
    BarcodeValues: any[],
    LabelSizeID: number,
    LabelSize: any,
    Row: number,
    Column: number,
    isQR: boolean,
  ): void {
    const { width: _Width, height: _Height, pageWidth: _PageWidth, verticalPitch: VerticalPitch,
      horizontalMargin: HorizontalMargin, columns: Columns, rows: Rows } = LabelSize;

    page._pageHeight = page._pageHeight + 0.3;
    page.pageHeight = page.pageHeight + 0.3;

    const columnWidth = _PageWidth / Columns;
    let dataBand: any;
    let posY = 0;
    let rows = 0;
    let FlagColumns = 1;
    let _columnWidth = 0;
    let FirstColumnWidth = 0;
    let FirstPosy = 0;
    const dbHeight = VerticalPitch;

    for (let i = 0; i < BarcodeValues.length; i++) {
      if (i === 0) {
        rows = Row - 1;
        dataBand = this._newDataBand(page, _Width, dbHeight, 'DataBand');

        ({ _columnWidth, FirstColumnWidth, FlagColumns } = this._calcAveryColumnOffset(
          LabelSizeID, Column, columnWidth
        ));

        posY = Row !== 1
          ? this._calcAveryRowOffset(LabelSizeID, Row, VerticalPitch)
          : this._averyFirstRowPosY(LabelSizeID);

        FirstPosy = posY;
      } else {
        const countI = Column === 1 ? i : (Column - 1) + i;
        posY = countI < Columns ? FirstPosy : (rows === 0 ? this._averyFirstRowPosY(LabelSizeID) : 0);
      }

      const barcode = new Stimulsoft.Report.BarCodes.StiBarCode(
        new Stimulsoft.Base.Drawing.RectangleD(HorizontalMargin + _columnWidth, posY, _Width, _Height)
      );
      barcode.codeValue = BarcodeValues[i].BarcodeNumber.toString();
      this._styleBarcode(barcode, isQR, _Height, LabelSizeID);
      dataBand.components.add(barcode);

      _columnWidth += this._averyColumnIncrement(LabelSizeID, columnWidth, FlagColumns);

      if ((FlagColumns % Columns) === 0) {
        rows++;
        _columnWidth = FirstColumnWidth;
        if (i !== BarcodeValues.length - 1) {
          dataBand = this._newDataBand(page, _Width, dbHeight, 'DataBand' + i);
        }
      }

      if (rows === Rows) rows = 0;
      FlagColumns++;
    }
  }

  // ─── Zebra pre-print ───────────────────────────────────────────────────────

  private _printZebraPrePrint(
    page: any,
    BarcodeValues: any[],
    LabelSizeID: number,
    LabelSize: any,
    isQR: boolean,
  ): void {
    page.margins.top = 0;

    const { width: _Width, height: _Height, verticalPitch: VerticalPitch } = LabelSize;

    for (let i = 0; i < BarcodeValues.length; i++) {
      const dataBand = new Stimulsoft.Report.Components.StiDataBand(
        new Stimulsoft.Base.Drawing.RectangleD(0, 0, _Width, _Height)
      );
      dataBand.name = 'DataBand' + i;
      page.components.add(dataBand);

      let rectX = 0;
      let BarcodeHeight = _Height;

      if (LabelSizeID === 15) rectX = -0.2;
      else if (LabelSizeID === 19) rectX = 0.1;

      if (LabelSizeID === 14 || LabelSizeID === 15 || LabelSizeID === 19) BarcodeHeight = 0.2;
      if (!LabelSizeID) BarcodeHeight = _Height;

      const bc = new Stimulsoft.Report.BarCodes.StiBarCode(
        new Stimulsoft.Base.Drawing.RectangleD(rectX, 0, Math.round(LabelSize.width), _Height)
      );
      bc.codeValue = BarcodeValues[i].BarcodeNumber.toString();

      if (!isQR) {
        bc.horAlignment = Stimulsoft.Base.Drawing.StiHorAlignment.Center;
        bc.vertAlignment = Stimulsoft.Base.Drawing.StiVertAlignment.Center;
        bc.autoScale = false;
        bc.showLabelText = true;
        bc.barCodeType = new Stimulsoft.Report.BarCodes.StiCode128AutoBarCodeType(10, BarcodeHeight);
      } else {
        bc.horAlignment = Stimulsoft.Base.Drawing.StiHorAlignment.Center;
        bc.vertAlignment = Stimulsoft.Base.Drawing.StiVertAlignment.Center;
        bc.autoScale = true;
        bc.showLabelText = true;
        bc.barCodeType = new Stimulsoft.Report.BarCodes.StiCode128AutoBarCodeType();
      }

      dataBand.components.add(bc);
    }

    page.orientation = Stimulsoft.Report.Components.StiPageOrientation.Portrait;
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  handleAveryMultipleCopies(data: any[], copies: number): any[] {
    return Array.from({ length: copies }, () => [...data]).flat();
  }

  skipRowsColumns(data: any[], SkipRows: number, SkipColumns: number, labelColumns: number): any[] {
    let empty = 0;
    if (SkipColumns > 1) empty += SkipColumns - 1;
    if (SkipRows > 1) empty += (SkipRows - 1) * labelColumns;
    for (let i = 0; i < empty; i++) data.unshift({});
    return data;
  }

  getDistinctSelectFieldsString(csv: string): string {
    const arr: string[] = [];
    csv.split(',').forEach(v => { if (v && !arr.includes(v)) arr.push(v); });
    return arr.join(',');
  }

  hasWhiteSpace(s: string): boolean {
    return [' ', '\t', '\n'].some(c => s.includes(c));
  }

  // ─── Private internal helpers ──────────────────────────────────────────────

  private _skipRowsAndColumns(response: any[]): any[] {
    if (this.barcodeModel.Row > 1 || this.barcodeModel.Column > 1) {
      return this.skipRowsColumns(
        response,
        this.barcodeModel.Row,
        this.barcodeModel.Column,
        this.barcodeModel.LabelSize?.columns,
      );
    }
    return response;
  }

  private _isZebra(id: number): boolean {
    return !id || (id >= 11 && id < 22);
  }

  private _calcHeightCode(height: number, allFields: string[], bcFields: string[]): number {
    let h = height / allFields.length;
    if (bcFields.length > 0) h -= 0.05;
    return h;
  }

  private _collectFields(): { allFields: string[]; allbarcodeFields: string[]; primaryQrField: string } {
    let rawSelectedFields = this._reportObject?.selectedFields || [];
    if (typeof rawSelectedFields === 'string') {
      rawSelectedFields = (rawSelectedFields as string).split(',').filter(s => s.trim().length > 0).map(s => ({ field: s.trim(), title: s.trim() }));
    } else if (rawSelectedFields.length === 0 && this._reportObject?.ReportObj?.selectedFields) {
      const rawStr = this._reportObject.ReportObj.selectedFields;
      if (typeof rawStr === 'string') {
        rawSelectedFields = rawStr.split(',').filter(s => s.trim().length > 0).map(s => ({ field: s.trim(), title: s.trim() }));
      }
    }

    const allFields: string[] = [];
    const allbarcodeFields: string[] = [];
    const isQR = this._isQR();
    const selectByField = this._getFilterByField();

    rawSelectedFields.forEach((el: any) => {
      const field = typeof el === 'string' ? el : el?.field || el?.title || '';
      const title = typeof el === 'string' ? el : el?.title || el?.field || '';
      const isBarcode = title && (title.includes('(barcode image)') || title.includes('(QRCode image)'));
      const suffix = title && title.includes('(QRCode image)') ? '(QRCode image)' : '(barcode image)';
      const cleanF = field.replace('(barcode image)', '').replace('(QRCode image)', '').trim();

      if (!isBarcode) {
        if (cleanF && !allFields.includes(cleanF)) allFields.push(cleanF);
      } else {
        if (cleanF && !allbarcodeFields.includes(cleanF + suffix)) allbarcodeFields.push(cleanF + suffix);
        if (cleanF && !allbarcodeFields.includes(cleanF)) allbarcodeFields.push(cleanF);
      }
    });

    const primaryQrField = (
      selectByField ||
      allbarcodeFields[0]?.replace('(QRCode image)', '').replace('(barcode image)', '').trim() ||
      allFields[0] ||
      'assetNumber'
    );

    if (isQR && allbarcodeFields.length === 0 && primaryQrField) {
      allbarcodeFields.push(primaryQrField);
      allbarcodeFields.push(primaryQrField + '(QRCode image)');
    }

    return { allFields, allbarcodeFields, primaryQrField };
  }

  private _resolveItemValue(item: any, fieldKey: string, fallback: string = ''): any {
    if (!item || !fieldKey) return fallback;
    const cleanKey = fieldKey.replace('(barcode image)', '').replace('(QRCode image)', '').trim();

    // 1. Direct match
    if (item[cleanKey] !== undefined && item[cleanKey] !== null && String(item[cleanKey]).trim() !== '') {
      return String(item[cleanKey]).trim();
    }

    // 2. Normalized matching
    const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const normKey = norm(cleanKey);

    for (const k of Object.keys(item)) {
      const nk = norm(k);
      if (normKey && nk === normKey) {
        if (item[k] !== undefined && item[k] !== null && String(item[k]).trim() !== '') {
          return String(item[k]).trim();
        }
      }
    }

    // 3. Fallbacks
    if (normKey.includes('id') || normKey.includes('barcode') || normKey.includes('code') || normKey.includes('number')) {
      const fallbackVal = item.BarcodeNumber ?? item.barcodeNumber ?? item.Barcode ?? item.barcode ?? item.code ?? item.id ?? item.AssetId ?? item.assetId ?? item.assetNumber ?? item.AssetNumber;
      if (fallbackVal !== undefined && fallbackVal !== null && String(fallbackVal).trim() !== '') {
        return String(fallbackVal).trim();
      }
    }

    if (normKey.includes('desc') || normKey.includes('name') || normKey.includes('title')) {
      const fallbackVal = item.displayName ?? item.DisplayName ?? item.Description ?? item.description ?? item.Name ?? item.name;
      if (fallbackVal !== undefined && fallbackVal !== null && String(fallbackVal).trim() !== '') {
        return String(fallbackVal).trim();
      }
    }

    return fallback;
  }

  private _mapResponse(response: any, allFields: string[], allbarcodeFields: string[] = []): any[] | object {
    if (response?.length > 0) {
      const isUrl = this._isUrlEnabled();
      const isQR = this._isQR();
      return response.slice(0, 20).map((item: any) => {
        const obj: any = {};
        allFields.forEach(f => (obj[f] = this._resolveItemValue(item, f)));
        allbarcodeFields.forEach(f => {
          if (isQR && isUrl) {
            obj[f] = this._buildQrUrl(item, f);
          } else {
            obj[f] = this._resolveItemValue(item, f);
          }
        });
        return obj;
      });
    }
    return this._buildEmptyModel(allFields, allbarcodeFields);
  }

  private _buildEmptyModel(fields: string[], barcodeFields: string[] = []): object {
    const obj: any = {};
    const isUrl = this._isUrlEnabled();
    const isQR = this._isQR();
    const sampleItem: any = {};
    fields.forEach(f => {
      const clean = f.replace('(barcode image)', '').replace('(QRCode image)', '').trim();
      sampleItem[clean] = (clean.toLowerCase().includes('id') || clean.toLowerCase().includes('code'))
        ? 'AST-1001'
        : `Sample ${clean}`;
      obj[f] = sampleItem[clean];
    });
    barcodeFields.forEach(f => {
      if (isQR && isUrl) {
        obj[f] = this._buildQrUrl(sampleItem, f);
      } else {
        const clean = f.replace('(barcode image)', '').replace('(QRCode image)', '').trim();
        obj[f] = (clean.toLowerCase().includes('id') || clean.toLowerCase().includes('code'))
          ? 'AST-1001'
          : `Sample ${clean}`;
      }
    });
    return obj;
  }

  private _applyAddressAndLogo(reportObj: any): void {
    if (!reportObj.addAddress && !reportObj.addLogo) return;
    const src = new Stimulsoft.Report.Dictionary.StiDataTableSource(
      'AddressAndLogo', 'AddressAndLogo', 'AddressAndLogo'
    );
    if (reportObj.addAddress)
      src.columns.add(new Stimulsoft.Report.Dictionary.StiDataColumn('SiteAddress', 'SiteAddress', 'SiteAddress'));
    if (reportObj.addLogo)
      src.columns.add(new Stimulsoft.Report.Dictionary.StiDataColumn('Logo', 'Logo', 'Logo'));
    this.stimulsoftReport.dictionary.dataSources.add(src);
  }

  private _setupPage(reportObj: any): any {
    const page = this.stimulsoftReport.pages.getByIndex(0);
    page._pageWidth = 8.5;
    page._pageHeight = 11;
    reportObj.orientation = reportObj.orientation || 'Portrait';
    page.orientation = reportObj.orientation === 'Portrait'
      ? Stimulsoft.Report.Components.StiPageOrientation.Portrait
      : Stimulsoft.Report.Components.StiPageOrientation.Landscape;
    return page;
  }

  private _buildBandStructure(
    page: any,
    allFields: string[],
    allbarcodeFields: string[],
    reportObject: any,
    isCatalog: boolean,
  ): void {
    // Simplified band builder – mirrors original createBarcodeCatalogPreview layout logic.
    const headerBand = new Stimulsoft.Report.Components.StiHeaderBand();
    headerBand.height = 0.2;
    headerBand.name = 'HeaderBand';
    page.components.add(headerBand);

    const dataBand = new Stimulsoft.Report.Components.StiDataBand();
    dataBand.dataSourceName = 'root';
    dataBand.height = 0.2;
    dataBand.name = 'DataBand';
    page.components.add(dataBand);

    const columnWidth = Stimulsoft.Base.StiAlignValue.alignToMinGrid(
      page.width / this.dataSet.tables.list[0].columns.count,
      page.width / this.dataSet.tables.list[0].columns.count,
      true,
    );
    let pos = 0;
    let nameIndex = 1;

    for (const index in this.dataSet.tables.list[0].columns.list) {
      const dataColumn = this.dataSet.tables.list[0].columns.list[index];
      const isBarcode = dataColumn.columnName.includes('(barcode image)') || dataColumn.columnName.includes('(QRCode image)');
      const colName = isBarcode
        ? dataColumn.columnName.replace('(barcode image)', '').replace('(QRCode image)', '').trim()
        : dataColumn.columnName;

      const hdr = new Stimulsoft.Report.Components.StiText();
      hdr.clientRectangle = new Stimulsoft.Base.Drawing.RectangleD(pos, 0, columnWidth, 0.2);
      hdr.text = dataColumn.caption || colName;
      hdr.name = 'HeaderText' + nameIndex;
      hdr.font = new Stimulsoft.System.Drawing.Font('Arial', 9, Stimulsoft.System.Drawing.FontStyle.Bold);
      hdr.horAlignment = Stimulsoft.Base.Drawing.StiTextHorAlignment.Center;
      hdr.border.side = Stimulsoft.Base.Drawing.StiBorderSides.Bottom;
      headerBand.components.add(hdr);

      const isQR = this._isQR();
      if (isBarcode) {
        const bc = new Stimulsoft.Report.BarCodes.StiBarCode(
          new Stimulsoft.Base.Drawing.RectangleD(pos, 0, columnWidth, isQR ? columnWidth : 0.4)
        );
        const label = this.hasWhiteSpace(dataColumn.columnName) || dataColumn.columnName.includes('.') || dataColumn.columnName.includes('(') || dataColumn.columnName.includes(')')
          ? `[${dataColumn.columnName}]` : dataColumn.columnName;
        bc.code = `{root.${label}}`;
        bc.name = 'DataText' + nameIndex;
        bc.horAlignment = Stimulsoft.Base.Drawing.StiHorAlignment.Center;
        bc.vertAlignment = Stimulsoft.Base.Drawing.StiVertAlignment.Center;
        if (isQR) {
          bc.autoScale = true;
          bc.showLabelText = true;
          bc.barCodeType = new Stimulsoft.Report.BarCodes.StiQRCodeBarCodeType();
        } else {
          bc.autoScale = false;
          bc.showLabelText = true;
          bc.barCodeType = new Stimulsoft.Report.BarCodes.StiCode128AutoBarCodeType(10, 0.5);
        }
        dataBand.components.add(bc);
      } else {
        const dt = new Stimulsoft.Report.Components.StiText();
        const lbl = this.hasWhiteSpace(colName) || colName.includes('.') || colName.includes('(') || colName.includes(')')
          ? `[${colName}]` : colName;
        dt.clientRectangle = new Stimulsoft.Base.Drawing.RectangleD(pos, 0, columnWidth, 0.2);
        dt.text = `{root.${lbl}}`;
        dt.name = 'DataText' + nameIndex;
        dt.horAlignment = Stimulsoft.Base.Drawing.StiTextHorAlignment.Center;
        dataBand.components.add(dt);
      }

      pos += columnWidth;
      nameIndex++;
    }

    page.invokeRendering();
  }

  private _applyLabelPageSettings(page: any, LabelSize: any): void {
    page.margins.left = LabelSize.pageLeftMargin;
    page.margins.bottom = LabelSize.pageBottomMargin;
    page.margins.right = LabelSize.pageRightMargin;
    page.margins.top = LabelSize.pageTopMargin;
    page._width = LabelSize.pageWidth;
    page._height = LabelSize.pageHeight;
    page._pageWidth = LabelSize.pageWidth;
    page._pageHeight = LabelSize.pageHeight;
    page.pageWidth = LabelSize.pageWidth;
    page.pageHeight = LabelSize.pageHeight;
    page.unlimitedHeight = false;
    page.reportUnit = Stimulsoft.Report.StiReportUnitType.Inches;
  }

  private _createLabelDataBand(page: any, LabelSize: any, LabelSizeID: number): any {
    const db = new Stimulsoft.Report.Components.StiDataBand();
    db.dataSourceName = 'root';
    db.height = page.height;
    db.name = 'DataBand';
    if (LabelSizeID) {
      db.columns = LabelSize.columns;
      db.columnWidth = LabelSize.width;
      db.columnGaps = LabelSize.columnGaps;
    }
    page.components.add(db);
    return db;
  }

  private _stripBarcodeFlag(dataColumn: any): { name: string; isBc: boolean; rawName: string } {
    const rawName = dataColumn.columnName;
    const isBc = rawName.includes('(barcode image)') || rawName.includes('(QRCode image)');
    const name = rawName.replace('(barcode image)', '').replace('(QRCode image)', '').trim();
    return { name, isBc, rawName };
  }

  private _addLabelComponent(
    dataBand: any, name: string, isBc: boolean,
    posY: number, width: number, height: number,
    isQR: boolean, idx: number, LabelSizeID: number,
    rawName?: string,
  ): void {
    if (isBc) {
      dataBand.components.add(this._makeBarcodeEl(rawName || name, 0, posY, width, height, isQR, idx, LabelSizeID));
    } else {
      dataBand.components.add(this._makeTextEl(name, 0, posY, width, height, idx));
    }
  }

  private _addZebraComponent(
    dataBand: any, name: string, isBc: boolean,
    posY: number, rectX: number, width: number, height: number,
    isQR: boolean, idx: number, LabelSizeID: number,
    rawName?: string,
  ): void {
    if (isBc) {
      dataBand.components.add(this._makeBarcodeEl(rawName || name, rectX, posY, width, height, isQR, idx, LabelSizeID));
    } else {
      dataBand.components.add(this._makeTextEl(name, rectX, posY, width, height, idx));
    }
  }

  private _isQR(): boolean {
    const p: any = (
      this.barcodeModel?.LabelPresetId ||
      this.barcodeModel?.labelPreset ||
      this.barcodeModel?.LabelPreset ||
      this._reportObject?.ReportObj?.LabelPreset ||
      this._reportObject?.ReportObj?.labelPreset ||
      this._reportObject?.ReportObj ||
      this._reportObject ||
      this.barcodeModel ||
      {}
    );

    const isQrFlag = (
      p.isQRCode ??
      p.isQrCode ??
      p.isQR ??
      p.isQr ??
      this.barcodeModel?.isQRCode ??
      this.barcodeModel?.isQrCode ??
      this.barcodeModel?.isQR ??
      this.barcodeModel?.isQr ??
      this._reportObject?.ReportObj?.isQRCode ??
      this._reportObject?.ReportObj?.isQrCode ??
      this._reportObject?.ReportObj?.isQR ??
      this._reportObject?.ReportObj?.isQr ??
      this._reportObject?.isQRCode ??
      this._reportObject?.isQrCode ??
      this._reportObject?.isQR ??
      this._reportObject?.isQr ??
      (p.labelType === 'publicQr' ? true : undefined) ??
      (p.twoDimensional === true || p.twoDimentional === true ? true : undefined)
    );
    if (isQrFlag !== undefined && isQrFlag !== null) return !!isQrFlag;

    const selectedFields = this._reportObject?.selectedFields || [];
    if (Array.isArray(selectedFields)) {
      return selectedFields.some((f: any) => {
        const t = f?.title || f?.field || (typeof f === 'string' ? f : '');
        return t.includes('(QRCode image)');
      });
    }
    return false;
  }

  private _isUrlEnabled(): boolean {
    const p: any = (
      this.barcodeModel?.LabelPresetId ||
      this.barcodeModel?.labelPreset ||
      this.barcodeModel?.LabelPreset ||
      this._reportObject?.ReportObj?.LabelPreset ||
      this._reportObject?.ReportObj?.labelPreset ||
      this._reportObject?.ReportObj ||
      this._reportObject ||
      this.barcodeModel ||
      {}
    );

    return !!(
      p.isURL ||
      p.isUrl ||
      p.labelType === 'publicQr' ||
      this.barcodeModel?.isURL ||
      this.barcodeModel?.isUrl ||
      this._reportObject?.ReportObj?.isURL ||
      this._reportObject?.ReportObj?.isUrl ||
      this._reportObject?.isURL ||
      this._reportObject?.isUrl ||
      this._reportObject?.selectedPreset?.isURL ||
      this._reportObject?.selectedPreset?.isUrl
    );
  }

  private _enrichResponse(response: any[]): any[] {
    if (!response || !Array.isArray(response) || response.length === 0) return response;
    const isUrl = this._isUrlEnabled();
    const isQR = this._isQR();
    const { allFields, allbarcodeFields, primaryQrField } = this._collectFields();

    return response.map((item: any) => {
      const obj: any = { ...item };
      allFields.forEach(f => {
        if (obj[f] === undefined) obj[f] = this._resolveItemValue(item, f);
      });
      allbarcodeFields.forEach(f => {
        if (isQR && isUrl) {
          obj[f] = this._buildQrUrl(item, f);
        } else if (obj[f] === undefined) {
          obj[f] = this._resolveItemValue(item, f);
        }
      });
      if (isQR && isUrl) {
        const qrUrl = this._buildQrUrl(item, primaryQrField);
        obj['BarcodeNumber'] = qrUrl;
        obj['QRCode'] = qrUrl;
        obj['qrcode'] = qrUrl;
        obj['Code'] = qrUrl;
        if (primaryQrField) {
          obj[primaryQrField] = qrUrl;
          obj[primaryQrField + '(QRCode image)'] = qrUrl;
        }
      }
      return obj;
    });
  }

  private _getReportCategoryId(): any {
    const directId = (
      this._reportObject?.ReportObj?.reportCategoryId ??
      this._reportObject?.ReportObj?.reportCategoryid ??
      this._reportObject?.ReportObj?.LabelPreset?.reportCategoryId ??
      this._reportObject?.ReportObj?.labelPreset?.reportCategoryId ??
      this._reportObject?.reportCategoryId ??
      this.barcodeModel?.reportCategoryId ??
      this.barcodeModel?.reportCategoryid ??
      this.barcodeModel?.LabelPresetId?.reportCategoryId ??
      this.barcodeModel?.labelPreset?.reportCategoryId
    );
    if (directId) return directId;

    const pageName = (
      this._reportObject?.ReportObj?.LabelPreset?.pageName ||
      this._reportObject?.ReportObj?.labelPreset?.pageName ||
      this._reportObject?.ReportObj?.pageName ||
      this._reportObject?.pageName ||
      this.barcodeModel?.LabelPresetId?.pageName ||
      this.barcodeModel?.labelPreset?.pageName ||
      this.barcodeModel?.pageName
    );
    if (pageName) return pageName;

    const ds = (
      this._reportObject?.ReportObj?.dataSource ||
      this._reportObject?.ReportObj?.sourceView ||
      this._reportObject?.ReportObj?.LabelPreset?.sourceView ||
      this._reportObject?.dataSource ||
      this.barcodeModel?.dataSource ||
      ''
    );
    if (ds) {
      return ds.includes('/') ? ds.substring(ds.lastIndexOf('/') + 1) : ds;
    }

    const isAsset = (
      this._reportObject?.ReportObj?.LabelPreset?.isAsset ||
      this._reportObject?.ReportObj?.isAsset ||
      this.barcodeModel?.isAsset
    );
    if (isAsset !== undefined) {
      return isAsset ? 'assets' : 'inventory';
    }

    return null;
  }

  private _getLabelId(): any {
    const raw = (
      this._reportObject?.ReportObj?.LabelPreset?.id ??
      this._reportObject?.ReportObj?.labelPreset?.id ??
      this._reportObject?.ReportObj?.labelPresetId ??
      this._reportObject?.ReportObj?.LabelPresetId ??
      this._reportObject?.ReportObj?.id ??
      (typeof this.barcodeModel?.LabelPresetId === 'object' ? (this.barcodeModel.LabelPresetId.id ?? this.barcodeModel.LabelPresetId.Id) : this.barcodeModel?.LabelPresetId) ??
      this.barcodeModel?.labelPreset?.id ??
      this.barcodeModel?.id ??
      null
    );
    return raw ? String(raw) : null;
  }

  private _getReportName(): string {
    return (
      this._reportObject?.ReportObj?.name ||
      this._reportObject?.ReportObj?.LabelPreset?.name ||
      this._reportObject?.ReportObj?.labelPreset?.name ||
      this._reportObject?.name ||
      this.barcodeModel?.name ||
      this.barcodeModel?.labelPreset?.name ||
      ''
    );
  }

  private _getFilterByField(): string {
    const raw = (
      this._reportObject?.ReportObj?.selectByField ||
      this._reportObject?.ReportObj?.filterBy ||
      this._reportObject?.selectByField ||
      this._reportObject?.filterBy ||
      this.barcodeModel?.selectByField ||
      this.barcodeModel?.filterBy ||
      this.barcodeModel?.LabelPresetId?.selectByField ||
      this.barcodeModel?.LabelPresetId?.filterBy ||
      this.barcodeModel?.labelPreset?.selectByField ||
      this.barcodeModel?.labelPreset?.filterBy ||
      this._reportObject?.ReportObj?.LabelPreset?.selectByField ||
      this._reportObject?.ReportObj?.LabelPreset?.filterBy ||
      ''
    );
    let clean = (typeof raw === 'object' ? (raw.field || raw.title || '') : String(raw))
      .replace('(barcode image)', '')
      .replace('(QRCode image)', '')
      .trim();

    if (clean.includes('.')) {
      clean = clean.substring(clean.lastIndexOf('.') + 1).trim();
    }
    return clean;
  }

  private _buildQrUrl(item: any, colName?: string): string {
    debugger
    let rawSelectedFields = this._reportObject?.selectedFields || [];
    if (typeof rawSelectedFields === 'string') {
      rawSelectedFields = (rawSelectedFields as string).split(',').filter(s => s.trim().length > 0).map(s => ({ field: s.trim(), title: s.trim() }));
    } else if (rawSelectedFields.length === 0 && this._reportObject?.ReportObj?.selectedFields) {
      const rawStr = this._reportObject.ReportObj.selectedFields;
      if (typeof rawStr === 'string') {
        rawSelectedFields = rawStr.split(',').filter(s => s.trim().length > 0).map(s => ({ field: s.trim(), title: s.trim() }));
      }
    }

    const allFieldObjs: { field: string; title: string }[] = [];
    const seenFields = new Set<string>();

    rawSelectedFields.forEach((f: any) => {
      const fieldStr = (typeof f === 'string' ? f : f.field || f.title || '').trim();
      const titleStr = (typeof f === 'string' ? f : f.title || f.field || '').trim();
      const cleanField = fieldStr.replace('(QRCode image)', '').replace('(barcode image)', '').trim();
      const cleanTitle = titleStr.replace('(QRCode image)', '').replace('(barcode image)', '').trim();

      if (cleanField && !seenFields.has(cleanField.toLowerCase())) {
        seenFields.add(cleanField.toLowerCase());
        allFieldObjs.push({ field: cleanField, title: cleanTitle || cleanField });
      }
    });

    if (allFieldObjs.length === 0 && colName) {
      const cleanCol = colName.replace('(barcode image)', '').replace('(QRCode image)', '').trim();
      allFieldObjs.push({ field: cleanCol, title: cleanCol });
    }

    if (allFieldObjs.length === 0) {
      allFieldObjs.push({ field: 'AssetId', title: 'Asset ID' });
    }

    const parts = allFieldObjs.map(f => {
      const val = this._resolveItemValue(item, f.field, 'AST-1001');
      return `${f.title}: ${val}`;
    }).join('<br/><br/>');

    const catId = this._getReportCategoryId();
    const labelId = this._getLabelId();
    const reportName = this._getReportName();
    const filterByField = this._getFilterByField() || (colName ? colName.replace('(barcode image)', '').replace('(QRCode image)', '').trim() : '');
    const filterVal = filterByField ? this._resolveItemValue(item, filterByField, '') : '';

    const payloadObj: any = {
      labelId: labelId || undefined,
      catId: catId || undefined,
      name: reportName || undefined,
      filterBy: filterByField || undefined,
      val: filterVal || undefined,
      data: parts
    };

    let encoded = '';
    try {
      encoded = btoa(unescape(encodeURIComponent(JSON.stringify(payloadObj))));
    } catch (e) {
      try {
        encoded = btoa(unescape(encodeURIComponent(parts)));
      } catch (e2) {
        encoded = btoa(parts);
      }
    }

    const queryParams: string[] = [`qrcode=${encodeURIComponent(encoded)}`];
    if (labelId) {
      queryParams.push(`labelId=${encodeURIComponent(labelId)}`);
    }
    if (catId) {
      queryParams.push(`cat=${encodeURIComponent(catId)}`);
    }
    if (filterByField) {
      queryParams.push(`field=${encodeURIComponent(filterByField)}`);
    }
    if (filterVal) {
      queryParams.push(`val=${encodeURIComponent(filterVal)}`);
    }
    if (reportName) {
      queryParams.push(`name=${encodeURIComponent(reportName)}`);
    }

    return `${window.location.origin}/decode-qr?${queryParams.join('&')}`;
  }

  private _makeBarcodeEl(
    colName: string, x: number, y: number, w: number, h: number,
    isQR: boolean, idx: number, LabelSizeID: number,
  ): any {
    const lbl = this.hasWhiteSpace(colName) || colName.includes('.') || colName.includes('(') || colName.includes(')') ? `[${colName}]` : colName;
    const bc = new Stimulsoft.Report.BarCodes.StiBarCode(
      new Stimulsoft.Base.Drawing.RectangleD(x, y, w, h)
    );
    bc.code = `{root.${lbl}}`;
    bc.name = 'DataText' + idx;
    this._styleBarcode(bc, isQR, h, LabelSizeID);
    return bc;
  }

  private _makeTextEl(
    colName: string, x: number, y: number, w: number, h: number, idx: number,
  ): any {
    const lbl = this.hasWhiteSpace(colName) || colName.includes('.') || colName.includes('(') || colName.includes(')') ? `[${colName}]` : colName;
    const dt = new Stimulsoft.Report.Components.StiText();
    dt.clientRectangle = new Stimulsoft.Base.Drawing.RectangleD(x, y, w, h);
    dt.text = `{root.${lbl}}`;
    dt.name = 'DT' + idx;
    dt.horAlignment = Stimulsoft.Base.Drawing.StiTextHorAlignment.Center;
    dt.vertAlignment = Stimulsoft.Base.Drawing.StiVertAlignment.Center;
    dt.font = new Stimulsoft.System.Drawing.Font('Arial', 10, Stimulsoft.System.Drawing.FontStyle.Regular);
    return dt;
  }

  private _styleBarcode(bc: any, isQR: boolean, height: number, LabelSizeID: number): void {
    bc.horAlignment = Stimulsoft.Base.Drawing.StiHorAlignment.Center;
    bc.vertAlignment = Stimulsoft.Base.Drawing.StiVertAlignment.Center;
    if (!isQR) {
      bc.autoScale = false;
      bc.showLabelText = true;
      bc.barCodeType = new Stimulsoft.Report.BarCodes.StiCode128AutoBarCodeType(10, height);
    } else {
      bc.autoScale = true;
      bc.showLabelText = true;
      bc.barCodeType = new Stimulsoft.Report.BarCodes.StiQRCodeBarCodeType();
    }
  }

  private _newDataBand(page: any, width: number, height: number, name: string): any {
    const db = new Stimulsoft.Report.Components.StiDataBand(
      new Stimulsoft.Base.Drawing.RectangleD(0, 0, width, height)
    );
    db.height = 0.5;
    db.name = name;
    page.components.add(db);
    return db;
  }

  private _detectFieldChanges(dataBand: any): {
    newFieldsDetected: boolean;
    listFieldNames: string[];
    listBarcodeFieldNames: string[];
  } {
    let newFieldsDetected = false;
    const listFieldNames: string[] = [];
    const listBarcodeFieldNames: string[] = [];

    if (!dataBand?.components) return { newFieldsDetected, listFieldNames, listBarcodeFieldNames };

    this._reportObject.selectedFields.forEach((el: any) => {
      const raw = this.hasWhiteSpace(el.field) || el.field.includes('.')
        ? `[${el.field}]` : el.field;
      const label = `{root.${raw}}`;
      const isBarcode = el.title && (el.title.includes('(barcode image)') || el.title.includes('(QRCode image)'));

      if (!isBarcode) {
        const found = dataBand.components.list.find(
          (x: any) => x.name.includes('DT') && x.text === label
        );
        if (!found) newFieldsDetected = true;
        listFieldNames.push(label);
      } else {
        const found = dataBand.components.list.find(
          (x: any) => x.name.includes('DataText') && x.code === label
        );
        if (!found) newFieldsDetected = true;
        listBarcodeFieldNames.push(label);
      }
    });

    return { newFieldsDetected, listFieldNames, listBarcodeFieldNames };
  }

  private _detectLabelSizeChange(): boolean {
    const saved = this.barcodeModel.savedLabelSize;
    const current = this.barcodeModel.LabelSize;
    if (!saved?.id && !current?.id) {
      return saved.width !== current.width || saved.height !== current.height;
    }
    return saved?.id !== current?.id;
  }

  private _isDefinitionOnly(): boolean {
    return !!this._reportObject?.definitionOnly;
  }

  // Avery column/row offset helpers (extracted from the monolithic switch blocks)
  private _calcAveryColumnOffset(
    LabelSizeID: number, Column: number, columnWidth: number
  ): { _columnWidth: number; FirstColumnWidth: number; FlagColumns: number } {
    let _columnWidth = 0;
    let FirstColumnWidth = 0;
    let FlagColumns = Column;

    const setTwoCol = (base: number, off: number) => {
      _columnWidth = Column !== 1 ? columnWidth * (Column - 1) - off : base;
      FirstColumnWidth = base;
    };

    switch (LabelSizeID) {
      case 1:  // Avery 5167 — 4 columns
        if (Column !== 1) {
          const offs = [0, 0, 0.05, 0.13, 0.2];
          _columnWidth = columnWidth * (Column - 1) - (offs[Column] ?? 0.2);
        } else { _columnWidth = 0.05; }
        FirstColumnWidth = 0.05; break;
      case 2:  // Avery 5160 — 3 columns
        if (Column !== 1) {
          _columnWidth = columnWidth * (Column - 1) - (Column === 2 ? 0.02 : 0.1);
        } else { _columnWidth = 0.08; }
        FirstColumnWidth = 0.08; break;
      case 5: case 6: case 7: case 9:  // 2-column Avery
        setTwoCol(0.02, 0.05); break;
      case 22: // Avery 6576 — 4 columns
        if (Column !== 1) {
          const offs22 = [0, 0, -0.04, 0.14, 0.34];
          _columnWidth = columnWidth * (Column - 1) - (offs22[Column] ?? 0.34);
        } else { _columnWidth = 0.22; }
        FirstColumnWidth = 0.22; break;
      case 10: _columnWidth = FirstColumnWidth = -0.04; break;
      case 8: setTwoCol(-0.05, 0.31); break;
      case 4: setTwoCol(-0.03, 0.8); break;
      case 3: setTwoCol(-0.07, 0.55); break;
    }

    return { _columnWidth, FirstColumnWidth, FlagColumns };
  }

  private _calcAveryRowOffset(LabelSizeID: number, Row: number, VerticalPitch: number): number {
    const offsets: Record<number, number> = {
      1: 0.4, 2: 0.95, 5: 0.94, 6: 0.9, 7: 1.94, 9: 3.22, 10: 4, 8: 3, 4: 2, 22: 1.2,
    };
    if (LabelSizeID === 3) {
      const r3: Record<number, number> = { 2: 2.1, 3: 1.95, 4: 1.75 };
      return Row * VerticalPitch - (r3[Row] ?? 0);
    }
    return Row * VerticalPitch - (offsets[LabelSizeID] ?? 0);
  }

  private _averyFirstRowPosY(LabelSizeID: number): number {
    if (LabelSizeID === 6) return 0.45;
    if (LabelSizeID === 10) return 0.05;
    if (LabelSizeID === 8) return 0.04;
    return 0.05;
  }

  private _averyColumnIncrement(LabelSizeID: number, colW: number, flag: number): number {
    const map: Record<number, number> = {
      1: flag === 2 ? colW - 0.08 : flag === 3 ? colW - 0.075 : colW - 0.095,
      2: colW - 0.1,
      5: colW - 0.05, 6: colW - 0.05, 7: colW - 0.05, 9: colW - 0.05,
      22: colW - 0.195,
      8: colW - 0.24,
      4: colW - 0.75,
      3: colW - 0.48,
    };
    return map[LabelSizeID] ?? colW - 0.05;
  }

  // ─── Record Selection methods ─────────────────────────────────────────────

  /** Open / close the "Select Records" panel. */
  toggleSelectRecords(): void {
    this.isSelectRecordsVisible = !this.isSelectRecordsVisible;
  }

  /**
   * Filter the grid as the user types.
   * Searches all string-valued columns (case-insensitive).
   */
  onSearchTermChange(term: string): void {
    this.selectSearchTerm = term;
    const lc = term.trim().toLowerCase();

    this.filteredSelectData = lc
      ? this.allReportData.filter(row =>
        this.selectGridColumns.some(col => {
          const v = row[col.field];
          return v != null && String(v).toLowerCase().includes(lc);
        })
      )
      : [...this.allReportData];
  }

  /** Check / uncheck every currently visible (filtered) row. */
  toggleSelectAll(checked: boolean): void {
    if (checked) {
      // Merge: add filtered rows that are not already selected.
      const existing = new Set(this.selectedRecords);
      this.filteredSelectData.forEach(r => existing.add(r));
      this.selectedRecords = Array.from(existing);
    } else {
      // Remove only the filtered rows; keep rows outside the current filter.
      const filteredSet = new Set(this.filteredSelectData);
      this.selectedRecords = this.selectedRecords.filter(r => !filteredSet.has(r));
    }
  }

  /**
   * Render the report using only the selected records.
   * Falls back to all records when nothing is selected.
   */
  applyRecordSelection(): void {
    const source = this.selectedRecords.length > 0
      ? this.selectedRecords
      : this.allReportData;

    this._executeRender(source);
  }

  /** Clear record selection and restore the full report. */
  clearRecordSelection(): void {
    this.selectedRecords = [];
    this.selectSearchTerm = '';
    this.filteredSelectData = [...this.allReportData];

    this._executeRender(this.allReportData);
  }

  // ─── Private helpers for Record Selection ─────────────────────────────────

  private _resetSelectionPanel(): void {
    this._buildSelectGridColumns();
    this.filteredSelectData = [...this.allReportData];
    this.selectedRecords = [];
  }

  /**
   * Build column definitions from the first data row.
   * Falls back to reportObject selectedFields titles when available.
   */
  private _buildSelectGridColumns(): void {
    if (!this.allReportData?.length) {
      this.selectGridColumns = [];
      return;
    }

    const fieldMeta: Record<string, string> = {};
    const fields = this._reportObject?.ReportObj?.selectedFields || this._reportObject?.selectedFields || [];
    fields.forEach((f: any) => {
      fieldMeta[f.field] = f.title ?? f.field;
    });

    this.selectGridColumns = Object.keys(this.allReportData[0]).map(field => ({
      field,
      title: fieldMeta[field] ?? field,
    }));
  }

  /**
   * Re-execute the last rendering function with a subset of data.
   */
  private _executeRender(data: any[]): void {
    debugger
    if (!this._lastRenderCall) return;

    this._initStimulsoft();
    this.dataSet = new Stimulsoft.System.Data.DataSet('DataSet');

    const { name, args } = this._lastRenderCall;
    const clonedArgs = [...args];

    if (name === 'createBarcodeCatalogPreview') {
      clonedArgs[0] = data;
      this.createBarcodeCatalogPreview(clonedArgs[0], true);
    } else if (name === 'createReportbarcodePreview_3') {
      clonedArgs[0] = data;
      this.createReportbarcodePreview_3(
        clonedArgs[0],
        clonedArgs[1],
        clonedArgs[2],
        clonedArgs[3],
        clonedArgs[4],
        clonedArgs[5],
        clonedArgs[6],
        true,
      );
    } else if (name === 'barcodePrintBarcodes') {
      clonedArgs[0] = data;
      this.barcodePrintBarcodes(
        clonedArgs[0],
        clonedArgs[1],
        clonedArgs[2],
        clonedArgs[3],
        clonedArgs[4],
        clonedArgs[5],
        clonedArgs[6],
        true,
      );
    } else if (name === 'openReportBarcodeLabelWithReportDefinitionWithoutReportObject') {
      clonedArgs[0] = data;
      this.openReportBarcodeLabelWithReportDefinitionWithoutReportObject(
        clonedArgs[0],
        clonedArgs[1],
        true,
        clonedArgs[3],
      );
    } else if (name === 'openReportBarcodeLabelWithReportDefinition') {
      clonedArgs[1] = data;
      this.openReportBarcodeLabelWithReportDefinition(
        clonedArgs[0],
        clonedArgs[1],
        true,
      );
    } else if (name === 'openReportBarcodeLabelWithReportDefinitionForEditBarcode') {
      clonedArgs[1] = data;
      this.openReportBarcodeLabelWithReportDefinitionForEditBarcode(
        clonedArgs[0],
        clonedArgs[1],
        true,
      );
    }
  }

  // Convenience getter used by the template "select all" checkbox.
  get allFilteredSelected(): boolean {
    if (!this.filteredSelectData.length) return false;
    return this.filteredSelectData.every(r => this.selectedRecords.includes(r));
  }

  get someFilteredSelected(): boolean {
    return this.selectedRecords.some(r => this.filteredSelectData.includes(r));
  }

  private _loadReportDefinition(def: string): void {
    if (!def || !this.stimulsoftReport) return;
    const trimmed = typeof def === 'string' ? def.trim() : '';
    if (trimmed.startsWith('{') || trimmed.startsWith('<')) {
      try {
        this.stimulsoftReport.load(trimmed);
        this._bindReportDefinitionComponents();
        return;
      } catch (e) {
        console.warn('Failed to load JSON report definition:', e);
      }
    }
    try {
      this.stimulsoftReport.loadEncryptedReport(def, environment.reportsKey);
      this._bindReportDefinitionComponents();
    } catch (e) {
      try {
        this.stimulsoftReport.load(def);
        this._bindReportDefinitionComponents();
      } catch (inner) {
        console.warn('Failed to load report definition:', inner);
      }
    }
  }

  private _bindReportDefinitionComponents(): void {
    if (!this.stimulsoftReport || !this.stimulsoftReport.pages) return;
    const isUrl = this._isUrlEnabled();
    const isQR = this._isQR();
    const { primaryQrField } = this._collectFields();

    for (let p = 0; p < this.stimulsoftReport.pages.count; p++) {
      const page = this.stimulsoftReport.pages.getByIndex(p);
      if (!page) continue;

      const traverseAndBind = (container: any) => {
        if (!container?.components?.list) return;
        container.components.list.forEach((c: any) => {
          if (c.ident === 'StiDataBand' || c.name?.includes('DataBand') || c instanceof Stimulsoft.Report.Components.StiDataBand) {
            c.dataSourceName = 'root';
          }
          if (c.ident === 'StiBarCode' || c.barCodeType || c instanceof Stimulsoft.Report.BarCodes.StiBarCode) {
            if (isQR) {
              c.barCodeType = new Stimulsoft.Report.BarCodes.StiQRCodeBarCodeType();
              c.autoScale = true;
              c.showLabelText = false;
            }
            c.code = `{root.BarcodeNumber}`;
            c.codeValue = `{root.BarcodeNumber}`;
          }
          traverseAndBind(c);
        });
      };
      traverseAndBind(page);
    }
  }
}